import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { useGetCheckinDefinitionById } from "../../../../api/queries";
import { useUpdateCheckinDefinition } from "../../../../api/mutations";
import { useNotification } from "../../../../hooks/useNotification";
import Container from "../../../../componentLibrary/Container";
import Button from "../../../../componentLibrary/Button";
import Spinner from "../../../../componentLibrary/Spinner";
import ReorderableVerticalList from "../../../../componentLibrary/ReorderableVerticalList";
import RbdFullRowDragSurface from "../../../../componentLibrary/RbdFullRowDragSurface";
import type { ToolboxQuestionI } from "../../../../types";
import QuestionTypeList from "../ProfileDefinitions/components/QuestionTypeList";
import QuestionCard from "../ProfileDefinitions/components/questionCards/QuestionCard";
import {
  getNewQuestion,
  QuestionType,
} from "../ProfileDefinitions/utils/questionUtils";
import RatingConfig from "./components/RatingConfig";
import type { RatingConfigState } from "./components/RatingConfig";

export default function EditCheckinDefinitionPage() {
  const { topicId, definitionId } = useParams<{
    topicId: string;
    definitionId: string;
  }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useNotification();

  const {
    data: definition,
    isLoading,
    isError,
  } = useGetCheckinDefinitionById(definitionId ?? "");

  const [questions, setQuestions] = useState<ToolboxQuestionI[] | null>(null);
  const [ratingConfig, setRatingConfig] = useState<RatingConfigState | null>(
    null,
  );

  const draft = questions ?? definition?.questions ?? [];

  const draftRating: RatingConfigState = ratingConfig ?? {
    ratingEnabled: definition?.ratingEnabled ?? true,
    ratingScale: definition?.ratingScale ?? 5,
    ratingLabel: definition?.ratingLabel ?? "",
  };

  const saveMutation = useUpdateCheckinDefinition({
    onSuccess: async () => {
      notify("success", "Check-in definition saved");
      await queryClient.invalidateQueries({
        queryKey: ["toolboxCheckinDefinitions", topicId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["toolboxCheckinDefinition", definitionId],
      });
      navigate(`/admin/toolboxes/${topicId}/checkin-definitions`);
    },
    onError: () => notify("error", "Failed to save check-in definition"),
  });

  const handleAdd = (type: QuestionType) => {
    setQuestions([...draft, getNewQuestion(type)]);
  };

  const handleChange = (updated: ToolboxQuestionI) => {
    setQuestions(
      draft.map((q) => (q.questionId === updated.questionId ? updated : q)),
    );
  };

  const handleRemove = (questionId: string) => {
    setQuestions(draft.filter((q) => q.questionId !== questionId));
  };

  const handleSave = () => {
    if (!definitionId) return;
    saveMutation.mutate({
      definitionId,
      questions: draft,
      ratingEnabled: draftRating.ratingEnabled,
      ratingScale: draftRating.ratingEnabled
        ? draftRating.ratingScale
        : undefined,
      ratingLabel: draftRating.ratingEnabled
        ? draftRating.ratingLabel
        : undefined,
    });
  };

  const backPath = `/admin/toolboxes/${topicId}/checkin-definitions`;

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="w-8" />
      </div>
    );
  }

  if (isError || !definition) {
    return (
      <div className="p-4 text-sm text-salmon-400">
        Check-in definition not found.
      </div>
    );
  }

  return (
    <div className="pt-4">
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(backPath)}
          className="flex items-center gap-1.5 text-sm text-electrum-300 hover:text-electrum transition-colors mb-4 w-fit"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Check-In Definitions
        </button>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            v{definition.version} · {draft.length} question
            {draft.length !== 1 ? "s" : ""}
          </span>
          <Button
            type="button"
            size="sm"
            loading={saveMutation.isPending}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </div>

      {definition.isActive && (
        <p className="text-sm text-amber-900 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-4">
          This is the <span className="font-semibold">live</span> check-in for
          all users. Saving applies immediately.
        </p>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-3 space-y-4 self-start">
          <Container>
            <QuestionTypeList onAdd={handleAdd} />
          </Container>
          <Container>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Rating
            </p>
            <RatingConfig config={draftRating} onChange={setRatingConfig} />
          </Container>
        </div>

        <div className="lg:col-span-9 space-y-4 pl-6">
          {draft.length === 0 ? (
            <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 py-20">
              <p className="text-sm text-gray-400">
                Add a question from the panel on the left.
              </p>
            </div>
          ) : (
            <ReorderableVerticalList
              items={draft}
              getId={(q) => q.questionId}
              onReorder={setQuestions}
              droppableId="toolbox-checkin-definition-questions-edit"
              className="space-y-4"
            >
              {(q, i, provided) => (
                <RbdFullRowDragSurface provided={provided}>
                  <QuestionCard
                    question={q}
                    index={i}
                    onChange={handleChange}
                    onRemove={() => handleRemove(q.questionId)}
                  />
                </RbdFullRowDragSurface>
              )}
            </ReorderableVerticalList>
          )}
        </div>
      </div>
    </div>
  );
}
